import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  HStack,
  VStack,
  Avatar,
  Badge,
  Icon,
  IconButton,
  useColorModeValue,
  Wrap,
  WrapItem,
  Tooltip,
} from '@chakra-ui/react';
import { EditIcon, ViewIcon } from '@chakra-ui/icons';
import { FaProjectDiagram, FaMapMarkerAlt, FaBriefcase } from 'react-icons/fa';
import { PhoneIcon } from '@chakra-ui/icons';

const EnhancedUsersTable = ({
  tableData = [],
  handleEditUser,
  handleRowClick,
  ...props
}) => {
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.800');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'purple';
      case 'schemeAdmin':
        return 'blue';
      case 'talukaUser':
        return 'green';
      case 'subDivisionalOfficer':
        return 'orange';
      case 'departmentOfficer':
        return 'teal';
      case 'projectOfficer':
        return 'cyan';
      default:
        return 'gray';
    }
  };

  const formatRole = (role) => {
    switch (role) {
      case 'talukaUser':
        return 'Tehsildar';
      case 'schemeAdmin':
        return 'Office In Charge (OIC)';
      case 'admin':
        return 'Admin';
      case 'subDivisionalOfficer':
        return 'Sub Divisional Officer (SDO)';
      case 'projectOfficer':
        return 'Project Officer (PO)';
      case 'departmentOfficer':
        return 'Department Officer';
      default:
        return role?.charAt(0).toUpperCase() + role?.slice(1) || 'N/A';
    }
  };

  // Helper function to get department names from array
  const getDepartmentNames = (department) => {
    if (!department) return ['N/A'];

    if (Array.isArray(department)) {
      return department.map((dept) =>
        typeof dept === 'object' ? dept.name || 'Department' : 'Department',
      );
    }

    // If it's a single object
    if (typeof department === 'object') {
      return [department.name || 'Department'];
    }

    return ['N/A'];
  };

  // Helper function to get scheme names
  const getSchemeNames = (schemes) => {
    if (!schemes || !Array.isArray(schemes)) return [];

    return schemes.map((scheme) =>
      typeof scheme === 'object' ? scheme.name || 'Scheme' : 'Scheme',
    );
  };

  // Helper function to get taluka names
  const getTalukaNames = (tahsils) => {
    if (!tahsils || !Array.isArray(tahsils)) return [];

    return tahsils.map((taluka) =>
      typeof taluka === 'object' ? taluka.name || 'Taluka' : 'Taluka',
    );
  };

  return (
    <Box overflowX="auto" {...props}>
      <Table variant="simple" size="md">
        <Thead bg={tableHeaderBg}>
          <Tr>
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              USER
            </Th>
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              ROLE
            </Th>
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              DEPARTMENT(S)
            </Th>
            {/* <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              ASSIGNMENTS
            </Th> */}
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              CONTACT
            </Th>
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
              textAlign="center"
            >
              STATUS
            </Th>
            <Th
              color={textColor}
              fontSize="xs"
              fontWeight="bold"
              py={3}
              borderBottom="1px solid"
              borderColor={borderColor}
              textAlign="center"
            >
              ACTIONS
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {tableData.map((user, idx) => {
            const departmentNames = getDepartmentNames(user.department);
            const schemeNames = getSchemeNames(user.schemes);
            const talukaNames = getTalukaNames(user.tahsils);

            return (
              <Tr
                key={user._id || idx}
                onClick={() => handleRowClick && handleRowClick(user)}
                cursor={handleRowClick ? 'pointer' : 'default'}
                _hover={{ bg: hoverBg }}
                transition="all 0.2s"
              >
                <Td py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <HStack spacing={3}>
                    <Avatar size="sm" name={user.name} bg="blue.500" />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        {user.name || 'N/A'}
                      </Text>
                      <Text fontSize="xs" color={subtitleColor}>
                        ID: {user.userId || 'N/A'}
                      </Text>
                    </VStack>
                  </HStack>
                </Td>
                <Td py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <Badge
                    colorScheme={getRoleColor(user.role)}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {formatRole(user.role)}
                  </Badge>
                </Td>
                <Td py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <VStack align="start" spacing={1}>
                    <HStack spacing={1}>
                      <Tooltip
                        label={departmentNames.join(', ')}
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="xs"
                        p={2}
                      >
                        <Box maxW="150px" overflow="hidden">
                          <Text
                            fontSize="xs"
                            color={subtitleColor}
                            noOfLines={1}
                            cursor="help"
                          >
                            {departmentNames.join(', ')}
                          </Text>
                        </Box>
                      </Tooltip>
                    </HStack>
                  </VStack>
                </Td>
                {/* <Td py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <VStack align="start" spacing={2}>
                    {schemeNames.length > 0 && (
                      <Box>
                        <HStack spacing={1}>
                          <Icon
                            as={FaProjectDiagram}
                            color="green.500"
                            boxSize={3}
                          />
                          <Text fontSize="xs" color={textColor}>
                            Schemes: {schemeNames.length}
                          </Text>
                        </HStack>
                        <Tooltip
                          label={schemeNames.join(', ')}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                          fontSize="xs"
                          p={2}
                        >
                          <Box maxW="150px" overflow="hidden">
                            <Text
                              fontSize="xs"
                              color={subtitleColor}
                              noOfLines={1}
                              cursor="help"
                            >
                              {schemeNames.join(', ')}
                            </Text>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}

                    {talukaNames.length > 0 && (
                      <Box>
                        <HStack spacing={1}>
                          <Icon
                            as={FaMapMarkerAlt}
                            color="orange.500"
                            boxSize={3}
                          />
                          <Text fontSize="xs" color={textColor}>
                            Talukas: {talukaNames.length}
                          </Text>
                        </HStack>
                        <Tooltip
                          label={talukaNames.join(', ')}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                          fontSize="xs"
                          p={2}
                        >
                          <Box maxW="150px" overflow="hidden">
                            <Text
                              fontSize="xs"
                              color={subtitleColor}
                              noOfLines={1}
                              cursor="help"
                            >
                              {talukaNames.join(', ')}
                            </Text>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}

                  </VStack>
                </Td> */}
                <Td py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <HStack spacing={2}>
                    <Icon as={PhoneIcon} color="blue.500" boxSize={3} />
                    <Text fontSize="sm" color={textColor}>
                      {user.phoneNo || 'N/A'}
                    </Text>
                  </HStack>
                </Td>
                <Td
                  py={3}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  textAlign="center"
                >
                  <Badge
                    colorScheme={user.active !== false ? 'green' : 'red'}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {user.active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td
                  py={3}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  textAlign="center"
                >
                  <HStack spacing={1} justify="center">
                    {handleEditUser && (
                      <IconButton
                        aria-label="Edit User"
                        icon={<EditIcon />}
                        size="xs"
                        colorScheme="blue"
                        variant="ghost"
                        borderRadius="md"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                      />
                    )}
                    {handleRowClick && (
                      <IconButton
                        aria-label="View Details"
                        icon={<ViewIcon />}
                        size="xs"
                        colorScheme="green"
                        variant="ghost"
                        borderRadius="md"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(user);
                        }}
                      />
                    )}
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default EnhancedUsersTable;
